import { useState } from "react";
import api from "../../api/axios";
import { updateSubmissionStatus } from "../../api/submissions";
import { useTranslation } from "../../context/TranslationContext";

/**
 * What can be done to a submission from any list: approve or reject it, re-check its
 * views, or appeal a rejection. `refresh` reloads the list; `setSubmissions` patches it.
 */
export default function useSubmissionActions({ setToast, refresh, setSubmissions }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [appealTarget, setAppealTarget] = useState(null);
  const [appealText, setAppealText] = useState("");
  const [appealing, setAppealing] = useState(false);

  const review = async (id, status) => {
    setBusy(id + status);
    try {
      const res = await updateSubmissionStatus(id, status);
      if (res.success) {
        setSubmissions((prev) => prev.map((row) => (row._id === id ? { ...row, status } : row)));
        setToast?.(t(`campaignFlow.status_${status}`));
      }
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const checkNow = async (id, dryRun = false) => {
    setBusy(`${id}check`);
    setCheckResult(null);
    try {
      const res = await api.post(`/submissions/${id}/check?save=${dryRun ? "false" : "true"}`);
      setCheckResult({ submissionId: id, ...res.data.data });
      if (!dryRun) {
        refresh?.();
        window.dispatchEvent(new CustomEvent("balance-updated"));
      }
    } catch (err) {
      setCheckResult({ submissionId: id, error: err?.response?.data?.message || err.message });
    } finally {
      setBusy(null);
    }
  };

  const openAppeal = (submission) => {
    setAppealTarget(submission);
    setAppealText("");
  };

  const submitAppeal = async () => {
    const message = appealText.trim();
    if (!appealTarget || !message) return;
    setAppealing(true);
    try {
      await api.post(`/submissions/${appealTarget._id}/appeal`, { message });
      setToast?.(t("mintSpace.appealSubmitted"));
      setAppealTarget(null);
      setAppealText("");
      refresh?.();
    } catch (err) {
      setToast?.(err?.response?.data?.message || t("common.error"));
    } finally {
      setAppealing(false);
    }
  };

  return {
    busy,
    review,
    checkNow,
    checkResult,
    closeCheckResult: () => setCheckResult(null),
    appealTarget,
    appealText,
    setAppealText,
    appealing,
    openAppeal,
    closeAppeal: () => !appealing && setAppealTarget(null),
    submitAppeal,
  };
}
